import React, { useEffect, useRef, useState } from 'react';
import statementService from '../../../services/statementService';
import { useT } from '../../../i18n/LanguageContext';
import { errorText, unwrap } from '../responseSafety';
import Banner from './Banner';
import Modal from './Modal';
import Skeleton from './Skeleton';

export default function MessageEditor({ clinic, onClose, onSaved }) {
  const t = useT();
  // A save that reported dropped characters has ALREADY been written server-side. Closing without
  // reloading would leave the list showing the old preview while the database holds the new text.
  const saved = useRef(false);
  const [text, setText] = useState('');
  const [maxChars, setMaxChars] = useState(2000);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  /**
   * Did the CURRENT text actually come from the server?
   *
   * Without this, a failed load left an empty textarea with Save enabled - and the server treats an
   * explicit empty string as "clear". An admin who saw a red banner and an empty box, assumed no
   * message had ever been set, and clicked Save would destroy that customer's negotiated credit
   * terms. There is no version history for this field anywhere.
   */
  const [loaded, setLoaded] = useState(false);
  /** What the terms said before this save. The server returns it because nothing else retains it -
   *  there is no version history on a clinic - so it is the only undo that exists. */
  const [previous, setPrevious] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await statementService.getMessage(clinic.clinicId);
        const body = unwrap(res, 'a credit limit message');
        if (!live) return;
        setText(body.message || '');
        if (body.maxCharacters) setMaxChars(body.maxCharacters);
        setLoaded(true);
      } catch (e) {
        if (live) setError(errorText(e, 'Could not load this message.'));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [clinic.clinicId]);

  const useTemplate = async () => {
    // Confirm before discarding text the operator typed. This field holds commercial terms the
    // server keeps no history of.
    if (
      text.trim() &&
      !window.confirm(t('Replace what you have typed with the standard wording?'))
    ) {
      return;
    }
    try {
      const res = await statementService.getTemplate();
      const body = unwrap(res, 'a template');
      setText(body.template || '');
    } catch (e) {
      setError(errorText(e, 'Could not load the template.'));
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await statementService.saveMessage(clinic.clinicId, text);
      const body = unwrap(res, 'a save result');
      // Surfaced, not swallowed: characters the statement cannot print are removed on save, and an
      // operator who pasted them needs to see that here rather than on a customer's bill.
      // The save has ALREADY committed at this point. If the stored text differs from what was
      // typed - characters removed, or runs of spaces collapsed so that what is stored is what will
      // print - the operator is shown the difference and the modal stays open. Whitespace collapsing
      // is not counted in charactersDropped, so comparing the text is the only way to catch it.
      const stored = body.cleared ? '' : body.message || '';
      saved.current = true;
      if (stored !== text) {
        setPrevious(body.previousMessage || '');
        setNotice(
          body.charactersDropped > 0
            ? t(
                'Saved. {count} character(s) could not be printed on a statement and were removed.',
                { count: body.charactersDropped }
              )
            : t(
                'Saved. The text was reformatted so that what is stored is exactly what will print.'
              )
        );
        setText(stored);
        setSaving(false);
        return;
      }
      onSaved();
    } catch (e) {
      setError(errorText(e, 'Could not save this message.'));
      setSaving(false);
    }
  };

  const clear = async () => {
    // CONFIRMED, because there is no undo and no version history for this field anywhere - the
    // save path at least hands back `previousMessage`, and this one does not. Replacing the text
    // with the standard wording already asks; permanently deleting negotiated terms did not.
    if (
      !window.confirm(
        t(
          'Remove the credit terms for {name}? Their statements will print no credit terms, and there is no way to recover the text.',
          { name: clinic.name }
        )
      )
    ) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await statementService.clearMessage(clinic.clinicId);
      onSaved();
    } catch (e) {
      setError(errorText(e, 'Could not clear this message.'));
      setSaving(false);
    }
  };

  /** Closing after a partial save still has to refresh the list behind it. */
  const close = () => (saved.current ? onSaved() : onClose());

  // ADVISORY ONLY - it never blocks Save. The server measures length after formatting, which both
  // expands some characters and removes others, so a 2100-character paste that sanitises under the
  // limit is perfectly valid. Disabling Save on this count left the operator with a red number, no
  // explanation and no way forward. A failed save displays authored guidance and keeps the text.
  const over = text.length > maxChars;
  const blocked = saving || loading || !loaded;

  return (
    <Modal
      labelledBy="credit-terms-title"
      onClose={close}
      // Escape must not abandon a save that is mid-flight - the write may already have committed,
      // and the list behind this modal would then be showing the old text.
      closeDisabled={saving}
      className="max-w-2xl max-h-[90vh] overflow-y-auto"
    >
      <>
        <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
          <div>
            <h2 id="credit-terms-title" className="text-[18px] font-bold text-[#0f172a]">
              {t('Credit limit and account terms')}
            </h2>
            <p className="text-[13px] text-[#64748b]">{clinic.name}</p>
          </div>
          <button
            onClick={close}
            disabled={saving}
            className="text-[#64748b] hover:text-[#0f172a] disabled:opacity-40"
            aria-label={t('Close')}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="px-6 py-4">
          {error && <Banner tone="error">{t(error)}</Banner>}
          {notice && (
            <Banner tone="warn">
              {t(notice)}
              {previous && (
                <button
                  onClick={() => {
                    setText(previous);
                    // The banner STAYS, and says what still has to happen. This button only fills
                    // the box; the save that produced this notice has already committed, so until
                    // the operator saves again the server still holds the reformatted text. The old
                    // handler cleared the notice, which removed the only thing on screen saying so.
                    setNotice(
                      t(
                        'The previous wording is back in the box, but it has not been saved yet - press Save to keep it.'
                      )
                    );
                    setPrevious('');
                  }}
                  className="ml-2 underline"
                >
                  {t('Put the previous wording back')}
                </button>
              )}
            </Banner>
          )}
          {loading ? (
            <Skeleton rows={4} />
          ) : (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                aria-label={t('Credit limit message')}
                placeholder={t('Your current credit limit is: $...')}
                className="w-full border border-[#e2e8f0] rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0089ff]/30"
              />
              <div className="flex items-center justify-between mt-2">
                <button
                  onClick={useTemplate}
                  className="text-[13px] text-[#0089ff] hover:underline"
                >
                  {t('Use the standard wording')}
                </button>
                <span className={`text-[12px] ${over ? 'text-[#f23e41]' : 'text-[#94a3b8]'}`}>
                  {text.length} / {maxChars}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#e2e8f0] flex items-center justify-between">
          <button
            onClick={clear}
            disabled={blocked || !text}
            className="text-[13px] text-[#f23e41] hover:underline disabled:opacity-40"
          >
            {t('Remove message')}
          </button>
          <div className="flex gap-2">
            <button
              onClick={close}
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg border border-[#e2e8f0] text-[#475569]"
            >
              {t('Cancel')}
            </button>
            <button
              onClick={save}
              disabled={blocked}
              className="px-4 py-2 text-sm rounded-lg bg-[#0089ff] text-white disabled:opacity-50"
            >
              {saving ? t('Saving...') : t('Save')}
            </button>
          </div>
        </div>
      </>
    </Modal>
  );
}
